import {AxiosInstance} from "axios"
import {analyzeEvent, getEvents, resolveEventTeam as resolveEventTeamRequest} from "./api/events"
import {getTeams} from "./api/team"
import {Event, EventStatusCode} from "../types/event"
import {TeamPreview} from "../types/team"

/**
 * The team search and the prediction api do not share their team ids, so a team is carried around by
 * name (the one displayed by the search api) until it gets resolved against /event-teams.
 */
export type AnalyzeTarget = {
    name: string
    countryCode?: string
    /** Search provider id of the club, when the caller has one: the surest way to pair the two */
    teamId?: number
}

/**
 * The api services write their parameters on `client.defaults`, so every call needs a client of its
 * own. Callers hand over the factory rather than a client for that reason.
 */
export type ClientFactory = () => Promise<AxiosInstance>

/** Accent marks, as split out by an NFD normalisation. Built from escapes so the source stays readable */
const COMBINING_MARKS = new RegExp("[\u0300-\u036f]", "g")

const normalizeName = (value: string) => (value || "")
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase()

/** Two names stand for the same club once spelling, accents and punctuation are set aside */
export const sameTeamName = (left?: string, right?: string) =>
    !!left && !!right && normalizeName(left) === normalizeName(right)

/** Matches still to be played come first, soonest first, then the played ones, most recent first */
export const sortByRelevance = (events: Event[]) => {
    const isUpcoming = (event: Event) => event.status_code === "NOT_STARTED" || event.status_code === "LIVE"
    const startedAt = (event: Event) => new Date(event.start_at).getTime()

    return [...events].sort((a, b) => {
        if (isUpcoming(a) !== isUpcoming(b)) return isUpcoming(a) ? -1 : 1
        return isUpcoming(a) ? startedAt(a) - startedAt(b) : startedAt(b) - startedAt(a)
    })
}

/**
 * Identity of a fixture.
 *
 * The id is enough on its own when the api sends one, the pair and the kick off tell two fixtures
 * apart when it does not.
 */
export const eventKey = (event: Event) =>
    `${event.id ?? ""}:${event.team_home?.id}:${event.team_away?.id}:${event.start_at}`

/** The same fixture, whichever object it is read from */
export const isSameEvent = (left?: Event, right?: Event) =>
    !!left && !!right && eventKey(left) === eventKey(right)

/** A fixture answered by both sides of a search is the same fixture, and is only worth showing once */
export const dedupeEvents = (events: Event[]) => {
    const seen = new Set<string>()

    return events.filter(event => {
        const key = eventKey(event)
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

/**
 * The team the search api holds under a given name.
 *
 * The prediction api names its teams in full but carries no crest, so a team read off a fixture is
 * looked up again against the search api, which is where the logo shown around the app comes from.
 * The search answers with near misses too, hence the preference for a name that matches.
 */
export const findTeamByName = async (newClient: ClientFactory, name: string): Promise<TeamPreview | undefined> => {
    const response = await getTeams(await newClient(), {search: name})
    const results = response?.response || []

    return (results.find(result => sameTeamName(result.team?.name, name)) || results[0])?.team
}

/**
 * The prediction provider's club behind a club of the team search.
 *
 * Resolved by the API from the search provider's team id, which is what carries the club's country
 * and its exact name: matching the two systems by the name shown on screen alone is what used to
 * leave real clubs reading as "not covered".
 */
export const resolveEventTeam = async (newClient: ClientFactory, target: AnalyzeTarget) => {
    const response = await resolveEventTeamRequest(await newClient(), target.teamId
        ? {team_id: target.teamId}
        : {name: target.name})

    return response?.data?.[0]
}

export const fetchEvents = async (newClient: ClientFactory, params: {
    team_home_id?: number
    team_away_id?: number
    status_code?: EventStatusCode
}) => {
    const response = await getEvents(await newClient(), params)
    return response?.data || []
}

/**
 * The analysis of a match, predictions included.
 *
 * Charged to the plan, so it is the API that decides what comes back: which fixture, whichever way
 * around it is scheduled, and whether the account may be served one at all.
 */
export const analyzeMatch = async (newClient: ClientFactory, homeId: number, awayId: number) =>
    analyzeEvent(await newClient(), {team_home_id: homeId, team_away_id: awayId})

/**
 * Events a team takes part in, whichever side it plays on.
 *
 * The prediction api filters on a fixed side, so the search is run twice — the team as the home side
 * and as the away side — and both answers are merged. An opponent, when given, is simply placed on
 * the other side of each search, which narrows the result down to the matches the two play together
 * without caring which of them the fixture lists as the host.
 */
export const searchTeamEvents = async (newClient: ClientFactory, teamId: number, opponentId?: number,
                                       statusCode?: EventStatusCode) => {
    const [asHome, asAway] = await Promise.all([
        fetchEvents(newClient, {team_home_id: teamId, team_away_id: opponentId, status_code: statusCode}),
        fetchEvents(newClient, {team_home_id: opponentId, team_away_id: teamId, status_code: statusCode})
    ])

    return sortByRelevance(dedupeEvents([...asHome, ...asAway]))
}
